"use client";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import GetInstantQuoteButton from "@/components/NewVersion/GetInstantQuoteButton";

// Banner 图片静态导入，获得 Next.js Image 优化（自动压缩、尺寸推断、模糊占位图）
import bannerOvermolding from "@/../public/image/new-version/home/banner/overmolding.png";
import bannerSandCasting from "@/../public/image/new-version/home/banner/sand-casting.png";
import bannerStamping from "@/../public/image/new-version/home/banner/stamping.png";
import bannerCncMilling from "@/../public/image/new-version/home/banner/cnc-milling.png";
import bannerWelding from "@/../public/image/new-version/home/banner/welding.png";

const BANNER_IMAGES = [bannerOvermolding, bannerSandCasting, bannerStamping, bannerCncMilling, bannerWelding];

// 弯曲风格的勾选图标：左侧线段向下右弯曲，右侧线段向上右弯曲，连接处圆滑无直角
const CheckIcon = () => (
	<svg
		className="h-5 w-5 flex-shrink-0 text-themePrimary400"
		viewBox="0 0 20 20"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M4 10 Q 7 14 9 12 Q 13 8 16 5" />
	</svg>
);

const Banner = () => {
	const t = useTranslations("newBanner");
	const { openModalInquiry } = useModalInquiryContext();

	const features = [t("feature1"), t("feature2"), t("feature3"), t("feature4"), t("feature5")];

	return (
		<section className="relative min-h-[400px] w-full overflow-hidden sm:min-h-[450px] md:min-h-[500px]">
			{/* 渐变背景 - 左白到右浅灰，过渡更柔和贴近参考 */}
			<div className="absolute inset-0 bg-[linear-gradient(120deg,_#fff_0%,_#fff_38%,_#a7aaaf_100%)]" />

			<div className="container relative z-10 px-4 pb-10 pt-8 font-sans sm:px-6 sm:pb-14 sm:pt-12 md:pb-16 md:pt-14 lg:pb-20 lg:pt-16">
				<div className="flex h-full w-full flex-col gap-8 lg:flex-row lg:gap-12">
					{/* 左侧内容区 */}
					<div className="max-w-3xl pr-2 sm:pr-4 md:pr-6 lg:pr-8 xl:min-w-[768px]">
						{/* Trustpilot 评分 */}
						<div className="-ml-[18px] inline-block">
							<div
								className="trustpilot-widget scale-125"
								data-locale="en-GB"
								data-template-id="5419b637fa0340045cd0c936"
								data-businessunit-id="68808142dd0a4c4d3e411b9c"
								data-style-height="24px"
								data-style-width="100%"
							>
								<a
									href="https://www.trustpilot.com/review/www.cncprotolabs.com?utm_medium=trustbox&utm_source=MicroTrustScore"
									target="_blank"
									rel="noopener"
								>
									Excellent Trustpilot
								</a>
							</div>
						</div>

						{/* 副标题 */}
						<p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-black sm:mb-3 sm:text-xs md:mb-4 md:text-sm lg:text-base">
							{t("subTitle")}
						</p>

						{/* 主标题 */}
						<h1 className="mb-4 text-xl font-bold leading-tight text-mainColor sm:mb-6 sm:text-[24px] md:mb-8 md:text-[30px] lg:text-[36px] xl:text-[44px]">
							{t("mainTitle")}
						</h1>

						{/* 特性列表 */}
						<ul className="mb-8 space-y-2 sm:mb-12 sm:space-y-2.5 md:mb-16 md:space-y-3 lg:mb-24">
							{features.map((feature, index) => (
								<li key={index} className="flex items-start gap-2 sm:gap-3">
									<CheckIcon />
									<span className="text-xs leading-relaxed text-gray-900 sm:text-sm md:text-base">
										{feature}
									</span>
								</li>
							))}
						</ul>

						{/* 按钮组 */}
						<div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
							<GetInstantQuoteButton />
							<button
								type="button"
								onClick={openModalInquiry}
								className="group inline-flex w-full items-center justify-center gap-2 rounded bg-mainColor px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 sm:w-fit sm:px-5 sm:py-3 md:px-10 md:py-3.5 md:text-base"
							>
								{t("startProductionQuote")}
							</button>
						</div>
					</div>

					{/* 右侧图片展示区 - 移动端隐藏 */}
					<div className="relative hidden w-full flex-1 xl:block">
						{/* 第1个零件 */}
						<Image
							src={BANNER_IMAGES[1]}
							alt="High-precision metal component"
							className="absolute left-[-5%] h-auto drop-shadow-[6px_10px_16px_rgba(0,0,0,0.2)] lg:top-[-8%] lg:w-[45%]"
							sizes="(max-width: 640px) 30vw, (max-width: 768px) 40vw, (max-width: 1200px) 25vw, 20vw"
							priority
						/>
						{/* 第2个零件 */}
						<Image
							src={BANNER_IMAGES[0]}
							alt="Precision CNC machined automotive part"
							className="absolute h-auto drop-shadow-[6px_10px_16px_rgba(0,0,0,0.2)] lg:right-[10%] lg:top-[8%] lg:w-[45%]"
							sizes="(max-width: 640px) 35vw, (max-width: 768px) 50vw, (max-width: 1200px) 30vw, 25vw"
							priority
						/>
						{/* 第三个零件 - 齿轮类 */}
						<Image
							src={BANNER_IMAGES[2]}
							alt="Precision gear component"
							className="absolute h-auto drop-shadow-[6px_10px_16px_rgba(0,0,0,0.2)] lg:left-[-25%] lg:top-[50%] lg:w-[45%]"
							sizes="(max-width: 640px) 22vw, (max-width: 768px) 30vw, (max-width: 1200px) 20vw, 15vw"
						/>

						{/* 第四个零件 */}
						<Image
							src={BANNER_IMAGES[3]}
							alt="Custom machined bracket"
							className="absolute h-auto drop-shadow-[6px_10px_16px_rgba(0,0,0,0.2)] lg:left-[18%] lg:top-[50%] lg:w-[45%]"
							sizes="(max-width: 640px) 18vw, (max-width: 768px) 25vw, (max-width: 1200px) 18vw, 13vw"
						/>

						{/* 第五个零件 */}
						<Image
							src={BANNER_IMAGES[4]}
							alt="Precision manufactured component"
							className="absolute h-auto drop-shadow-[6px_10px_16px_rgba(0,0,0,0.2)] lg:right-[0%] lg:top-[50%] lg:w-[44%]"
							sizes="(max-width: 640px) 20vw, (max-width: 768px) 28vw, (max-width: 1200px) 18vw, 14vw"
						/>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Banner;
